import { on, off, fire } from 'delegated-events'
import { createUrl } from '@common/util'
import scrollTo from '@common/scroll-to'
import ajax from '@common/ajax'

const selectors = {
  pageFilters: '[data-page-filters]',
}

const resetForm = (form) => {
  form.querySelectorAll('input').forEach((input) => (input.value = null))
  form.querySelectorAll('select').forEach((select) => (select.selectedIndex = -1))
}

const handleClear = (e) => {
  e.preventDefault()

  const filters = document.querySelector(selectors.pageFilters)

  filters.querySelectorAll('form').forEach((form) => resetForm(form))

  window.history.replaceState({}, '', e.target.href)

  fire(document.documentElement, 'pagefilters:update', { filtersEl: filters, url: window.location.href, type: 'clear' })
}

const handleFormSubmit = (e) => {
  const form = e.target.closest('form')
  const filters = form.closest(selectors.pageFilters)

  // Use the current url and update the query params
  if (form.dataset.submit && form.dataset.submit === 'append') {
    e.preventDefault()

    const data = new FormData(form)
    const url = createUrl(window.location, data, true)

    window.history.replaceState({}, '', url)

    fire(document.documentElement, 'pagefilters:update', {
      filtersEl: filters,
      url: window.location.href,
      type: 'append',
    })

    return
  }

  scrollTo(filters, 48)
}

const handleChange = (e) => {
  const parentForm = e.target.closest('form')

  if (parentForm) {
    if (parentForm.requestSubmit) {
      parentForm.requestSubmit()
    } else {
      parentForm.dispatchEvent(new Event('submit'))
    }
  }
}

const handleUpdate = async (e) => {
  e.stopPropagation()

  const pageContent = document.querySelector('[data-page-content]')

  if (pageContent) {
    pageContent.classList.add('is-loading')

    const res = await ajax.get(e.detail.url).text()

    pageContent.innerHTML = res
    pageContent.classList.remove('is-loading')

    fire(document.documentElement, 'pagecontent:replace')
  }
}

const setupDropdowns = async (holder) => {
  const dropdowns = holder.querySelectorAll('.js-dropdown')

  if (!dropdowns.length) {
    return
  }

  const { default: easydropdown } = await import('easydropdown-a11y')
  import('@styles/components/dropdown.css')

  dropdowns.forEach((el) => {
    const select = el

    if (!select) {
      return
    }

    const placeholder = el.querySelector('option[value=""]')

    if (placeholder && !placeholder.hasAttribute('data-placeholder')) {
      placeholder.setAttribute('data-placeholder', true)
    }

    const opts = {
      behavior: {
        openOnFocus: true,
        maxVisibleItems: 10,
        liveUpdates: true,
        useNativeUiOnMobile: false,
      },
    }

    easydropdown(select, opts)
  })
}

const setupDatetimeFields = async (holder) => {
  const fields = holder.querySelectorAll('.date-field')

  if (!fields.length) {
    return
  }

  const { default: flatpickr } = await import('flatpickr')
  import('flatpickr/dist/flatpickr.css')
  import('@styles/components/datepicker.css')

  fields.forEach((el) => {
    flatpickr(el, {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'M J',
      disableMobile: true,
      maxDate: new Date(),
      monthSelectorType: 'static',
      mode: 'range',
      position: 'below left',
      onChange: (_, __, instance) => {
        if (!instance.isOpen) {
          handleChange({ target: el })
        }
      },
    })
  })
}

const setupPageFilters = (el) => {
  setupDropdowns(el)
  setupDatetimeFields(el)
}

const setup = (reInit) => {
  const els = document.querySelectorAll(selectors.pageFilters)

  const submitArgs = [
    'submit',
    `${selectors.pageFilters} form:not([data-submit="live"])`,
    handleFormSubmit,
    { capture: true },
  ]

  const changeArgs = ['change', `${selectors.pageFilters} select`, handleChange]
  const clearArgs = ['click', '[data-page-filters-clear]', handleClear]
  const updateArgs = ['pagefilters:update', 'html', handleUpdate]

  // Sometimes we refresh the html and need to remove the old listeners before adding them again
  if (reInit) {
    off(...submitArgs)
    off(...changeArgs)
    off(...clearArgs)
    off(...updateArgs)
  }

  on(...submitArgs)
  on(...changeArgs)
  on(...clearArgs)
  on(...updateArgs)

  els.forEach((el) => setupPageFilters(el))
}

setup()

on('pagefilters:reinit', 'html', () => setup(true))
